import { apiServicesConfig, getServiceBaseURL } from '@/config/api.config';
import type { RequestConfig } from '@/types/api.config';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import axios from 'axios';
import { createAxiosInstance } from './axios.instance';

// API service class
class ApiService {
  private instances: Map<string, AxiosInstance> = new Map();

  constructor() {
    // Initialize all service instances
    this.initializeInstances();
  }

  // Initialize all service instances
  private initializeInstances(): void {
    apiServicesConfig.forEach((config) => {
      const baseURL = getServiceBaseURL(config.key);
      const instance = createAxiosInstance(baseURL, config.timeout);
      console.log(
        `Initialized API service '${config.key}' with baseURL: ${baseURL}`
      );
      this.instances.set(config.key, instance);
    });
  }

  // Get axios instance for a service key
  private getInstance(serviceKey: string): AxiosInstance {
    const instance = this.instances.get(serviceKey);
    if (!instance) {
      throw new Error(`API service '${serviceKey}' not initialized`);
    }
    return instance;
  }

  // General request method for API service with type inference
  private async request<T>(
    serviceKey: string,
    method: string,
    url: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<T> {
    const instance = this.getInstance(serviceKey);

    const axiosConfig: AxiosRequestConfig = {
      method,
      url,
      headers: config?.headers,
      timeout: config?.timeout,
      withCredentials: config?.withCredentials,
    };

    if (method.toLowerCase() === 'get') {
      axiosConfig.params = data
        ? { ...config?.params, ...(data as Record<string, unknown>) }
        : config?.params;
    } else {
      axiosConfig.data = data;
    }

    const response = await instance.request<T>(axiosConfig);
    return response.data;
  }

  // GET request method for API service with type inference
  async get<T>(
    serviceKey: string,
    url: string,
    params?: Record<string, unknown>,
    config?: RequestConfig
  ): Promise<T> {
    return this.request<T>(serviceKey, 'get', url, params, config);
  }

  // POST request method for API service with type inference
  async post<T>(
    serviceKey: string,
    url: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<T> {
    return this.request<T>(serviceKey, 'post', url, data, config);
  }

  // PUT request method for API service with type inference
  async put<T>(
    serviceKey: string,
    url: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<T> {
    return this.request<T>(serviceKey, 'put', url, data, config);
  }

  // DELETE request method for API service with type inference
  async delete<T>(
    serviceKey: string,
    url: string,
    params?: Record<string, unknown>,
    config?: RequestConfig
  ): Promise<T> {
    return this.request<T>(serviceKey, 'delete', url, params, config);
  }

  // PATCH request method for API service with type inference
  async patch<T>(
    serviceKey: string,
    url: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<T> {
    return this.request<T>(serviceKey, 'patch', url, data, config);
  }
}

// Create singleton instance of ApiService
const apiService = new ApiService();

// Service method interface
interface ServiceMethods {
  get: <T>(url: string, params?: unknown, config?: RequestConfig) => Promise<T>;
  post: <T>(url: string, data?: unknown, config?: RequestConfig) => Promise<T>;
  put: <T>(url: string, data?: unknown, config?: RequestConfig) => Promise<T>;
  delete: <T>(
    url: string,
    params?: unknown,
    config?: RequestConfig
  ) => Promise<T>;
  patch: <T>(url: string, data?: unknown, config?: RequestConfig) => Promise<T>;
}

// Create service methods dynamically
const createServiceMethods = (serviceKey: string): ServiceMethods => ({
  get: <T>(url: string, params?: unknown, config?: RequestConfig) =>
    apiService.get<T>(
      serviceKey,
      url,
      params as Record<string, unknown>,
      config
    ),
  post: <T>(url: string, data?: unknown, config?: RequestConfig) =>
    apiService.post<T>(serviceKey, url, data, config),
  put: <T>(url: string, data?: unknown, config?: RequestConfig) =>
    apiService.put<T>(serviceKey, url, data, config),
  delete: <T>(url: string, params?: unknown, config?: RequestConfig) =>
    apiService.delete<T>(
      serviceKey,
      url,
      params as Record<string, unknown>,
      config
    ),
  patch: <T>(url: string, data?: unknown, config?: RequestConfig) =>
    apiService.patch<T>(serviceKey, url, data, config),
});

// Export convenient API call methods
// Dynamically generate service methods from apiServicesConfig
export const api = apiServicesConfig.reduce(
  (acc, config) => {
    acc[config.key as keyof typeof acc] = createServiceMethods(config.key);
    return acc;
  },
  {} as Record<string, ServiceMethods>
);

// File service
api.file = axios.create({
  baseURL: import.meta.env.PUBLIC_API_CFS_URL,
  timeout: 30000,
}) as ServiceMethods;

export default apiService;
